import { useState, useCallback } from 'react'
import {
  exportNoteAsZip,
  exportNotesAsZip,
  downloadBlob,
  downloadFile,
  generateNoteText,
  generateNoteMarkdown,
  type ExportNote,
  type ExportOptions,
  type ExportProgress,
} from '@/lib/export'
import type { Note, NoteImage } from '@/types'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry.tsx'

// ============================================================
// 类型定义
// ============================================================

interface ExportState {
  isExporting: boolean
  progress: ExportProgress | null
  error: string | null
}

interface UseExportReturn extends ExportState {
  // 导出单篇笔记
  exportNote: (
    note: Note, 
    images?: NoteImage[], 
    options?: ExportOptions & { shareImageDataUrl?: string; shareImageFilename?: string }
  ) => Promise<void>
  // 批量导出笔记
  exportNotes: (notes: ExportNote[], options?: ExportOptions) => Promise<void>
  // 下载文案文本
  downloadText: (note: Note, format?: 'txt' | 'md') => void
  // 复制文案到剪贴板
  copyToClipboard: (note: Note) => Promise<boolean>
}

// ============================================================
// Hook 实现
// ============================================================

export function useExport(): UseExportReturn {
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState<ExportProgress | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * 导出单篇笔记为 ZIP
   */
  const exportNote = useCallback(async (
    note: Note,
    images: NoteImage[] = [],
    options?: ExportOptions & { shareImageDataUrl?: string; shareImageFilename?: string }
  ): Promise<void> => {
    setIsExporting(true)
    setError(null)
    setProgress({ current: 0, total: 1, message: '准备导出...' })

    try {
      // 准备图片数据
      const imageData = images
        .filter(img => img.url && img.status === 'completed')
        .map((img, index) => ({
          url: img.url!,
          filename: `图片_${String(index + 1).padStart(2, '0')}.jpg`,
        }))

      const blob = await exportNoteAsZip(note, imageData, options, setProgress)
      
      // 生成文件名
      const sanitizedTitle = note.title
        .replace(/[<>:"/\\|?*]/g, '')
        .slice(0, 30)
      const filename = `${sanitizedTitle}_${formatDate(new Date())}.zip`
      
      downloadBlob(blob, filename)
      toast.success('导出成功')
    } catch (err) {
      captureError(err, { feature: 'export', action: 'export-single', noteId: note.id })
      const message = err instanceof Error ? err.message : '导出失败'
      setError(message)
      toast.error(message)
    } finally {
      setIsExporting(false)
      setProgress(null)
    }
  }, [])

  /**
   * 批量导出笔记为 ZIP
   */
  const exportNotes = useCallback(async (
    notes: ExportNote[],
    options?: ExportOptions
  ): Promise<void> => {
    if (notes.length === 0) {
      toast.error('没有可导出的笔记')
      return
    }

    setIsExporting(true)
    setError(null)
    setProgress({ current: 0, total: notes.length, message: '准备导出...' })

    try {
      const blob = await exportNotesAsZip(notes, options, setProgress)
      
      const filename = `小红书笔记_${notes.length}篇_${formatDate(new Date())}.zip`
      downloadBlob(blob, filename)
      toast.success(`成功导出 ${notes.length} 篇笔记`)
    } catch (err) {
      captureError(err, { feature: 'export', action: 'export-batch', noteCount: notes.length })
      const message = err instanceof Error ? err.message : '导出失败'
      setError(message)
      toast.error(message)
    } finally {
      setIsExporting(false)
      setProgress(null)
    }
  }, [])

  /**
   * 下载单篇笔记的文案
   */
  const downloadText = useCallback((note: Note, format: 'txt' | 'md' = 'txt') => {
    const content = format === 'md' 
      ? generateNoteMarkdown(note) 
      : generateNoteText(note)
    
    const sanitizedTitle = note.title
      .replace(/[<>:"/\\|?*]/g, '')
      .slice(0, 30)
    const filename = `${sanitizedTitle}.${format}`
    
    downloadFile(content, filename, format === 'md' ? 'text/markdown' : 'text/plain')
    toast.success('下载成功')
  }, [])

  /**
   * 复制文案到剪贴板
   */
  const copyToClipboard = useCallback(async (note: Note): Promise<boolean> => {
    try {
      const text = generateNoteText(note)
      await navigator.clipboard.writeText(text)
      toast.success('已复制到剪贴板')
      return true
    } catch {
      toast.error('复制失败')
      return false
    }
  }, [])

  return {
    isExporting,
    progress,
    error,
    exportNote,
    exportNotes,
    downloadText,
    copyToClipboard,
  }
}

// ============================================================
// 辅助函数
// ============================================================

/**
 * 格式化日期为文件名友好格式
 */
function formatDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')
  return `${year}${month}${day}_${hour}${minute}`
}

// ============================================================
// 便捷导出函数
// ============================================================

/**
 * 快速导出笔记（不使用 Hook）
 */
export async function quickExportNote(
  note: Note, 
  images: NoteImage[] = []
): Promise<void> {
  const imageData = images
    .filter(img => img.url && img.status === 'completed')
    .map((img, index) => ({
      url: img.url!,
      filename: `图片_${String(index + 1).padStart(2, '0')}.jpg`,
    }))

  const blob = await exportNoteAsZip(note, imageData)
  
  const sanitizedTitle = note.title
    .replace(/[<>:"/\\|?*]/g, '')
    .slice(0, 30)
  const filename = `${sanitizedTitle}_${formatDate(new Date())}.zip`
  
  downloadBlob(blob, filename)
}

